import type { InsightActionGroup, InsightActionRow } from '../models/insightAction.types'

export const SELECTION_CEILING = 200

export interface RowFilters {
  channel: string | null
  onlyApplicable: boolean
}

export function isPickable(row: InsightActionRow): boolean {
  return row.actionable && row.eligibility?.state !== 'blocked'
}

export function addressedRows(group: InsightActionGroup | null): InsightActionRow[] {
  return (group?.rows ?? []).filter((row) => row.reasonCode !== 'filtered_out')
}

export function filteredOutRows(group: InsightActionGroup | null): InsightActionRow[] {
  return (group?.rows ?? []).filter((row) => row.reasonCode === 'filtered_out')
}

export function listedRows(
  group: InsightActionGroup | null,
  groupCount: number,
): InsightActionRow[] {
  return groupCount > 1 ? addressedRows(group) : (group?.rows ?? [])
}

export function pickableRows(rows: InsightActionRow[]): InsightActionRow[] {
  return rows.filter((row) => isPickable(row))
}

export function canFilterByChannel(rows: InsightActionRow[]): boolean {
  return rows.some((row) => Boolean(row.channelKey))
}

export function visibleRows(rows: InsightActionRow[], filters: RowFilters): InsightActionRow[] {
  const pickable = new Set(pickableRows(rows).map((row) => row.rowKey))
  let out = rows
  if (filters.onlyApplicable) out = out.filter((row) => pickable.has(row.rowKey))
  if (filters.channel) out = out.filter((row) => row.channelKey === filters.channel)
  return [...out].sort((a, b) => Number(pickable.has(b.rowKey)) - Number(pickable.has(a.rowKey)))
}

export function coverageColumn(rows: InsightActionRow[]): string {
  const first = rows[0]
  if (!first) return ''
  return (
    Object.keys(first.columns ?? {}).find(
      (key) => /cost|spend/i.test(key) && typeof first.columns[key] === 'number',
    ) ?? ''
  )
}

export function selectedCoverage(rows: InsightActionRow[], selection: ReadonlySet<string>): number {
  const key = coverageColumn(rows)
  if (!key) return 0
  return rows.reduce(
    (sum, row) => (selection.has(row.rowKey) ? sum + Number(row.columns[key] ?? 0) : sum),
    0,
  )
}

export function hiddenSelectedCount(
  visible: InsightActionRow[],
  selection: ReadonlySet<string>,
): number {
  const shown = new Set(visible.map((row) => row.rowKey))
  return [...selection].filter((key) => !shown.has(key)).length
}

export function selectAllKeys(rows: InsightActionRow[]): string[] {
  return pickableRows(rows)
    .map((row) => row.rowKey)
    .slice(0, SELECTION_CEILING)
}

export function columnTracks(rows: InsightActionRow[], columnKeys: string[]): string {
  const tracks = columnKeys.map((key) =>
    isMeasureColumn(rows, key) ? 'minmax(4rem,6rem)' : 'minmax(6rem,1fr)',
  )
  // The first track carries the checkbox AND the table's leading padding — 1.5rem fits the box
  // alone and clips it once the cell is padded.
  return `2.75rem minmax(8rem,2fr) ${tracks.join(' ')}`
}

function isMeasureColumn(rows: InsightActionRow[], key: string): boolean {
  const filled = rows
    .map((row) => row.columns?.[key])
    .filter((value) => value !== null && value !== undefined && value !== '')
  return filled.length > 0 && filled.every((value) => typeof value === 'number')
}

export function visibleColumnKeys(rows: InsightActionRow[]): string[] {
  const first = rows[0]
  if (!first) return []
  return Object.keys(first.columns ?? {}).filter((key) =>
    rows.some((row) => {
      const value = row.columns?.[key]
      return value !== null && value !== undefined && value !== ''
    }),
  )
}
